import React, { useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { AE_CARD } from '@/components/assessment-engine/layout/aeTheme';
import { formatScore } from '@/components/assessment-engine/resultsScoreUtils';
import type { AeMaturityPayload } from '@/components/assessment-engine/template-builder/maturityMapUtils';
import type { AeSectionScore } from '@/types/assessmentEngine';

interface ResultsMaturityStripProps {
  maturity: AeMaturityPayload;
  /** Section order / names from the score rollup (same as spider table). */
  sections: Array<Pick<AeSectionScore, 'section_id' | 'section_name' | 'mean_score'>>;
  /** Assessment scale maximum for “score / max” display. */
  scaleMax: number;
  /** When true, headline uses “Overall capability…” (admin); else “Your capability…”. */
  overallHeadline?: boolean;
  showBenchmark: boolean;
}

/**
 * Capability headline (band + raw score / max) and dimension list as
 * “Dimension name (Band label)”.
 */
export const ResultsMaturityStrip: React.FC<ResultsMaturityStripProps> = ({
  maturity,
  sections,
  scaleMax,
  overallHeadline = false,
  showBenchmark,
}) => {
  const { t } = useLanguage();
  const band = maturity.overall.label?.trim() || '—';
  const score = formatScore(maturity.overall.mean_score ?? null);
  const peer = showBenchmark ? maturity.benchmark_b1 : null;
  const modelMin = ((maturity.model.min_pct / 100) * scaleMax).toFixed(1);
  const modelMax = ((maturity.model.max_pct / 100) * scaleMax).toFixed(1);

  const dimensionRows = useMemo(() => {
    const byId = new Map(maturity.sections.map((s) => [s.section_id, s]));
    return sections.map((sec) => {
      const mat = byId.get(sec.section_id);
      return {
        section_id: sec.section_id,
        section_name: sec.section_name,
        label: mat?.label?.trim() || '—',
      };
    });
  }, [maturity.sections, sections]);

  return (
    <section className={`${AE_CARD} p-3 space-y-3`}>
      <p className="text-base font-semibold text-[var(--color-surface-text)]">
        {overallHeadline
          ? t('assessmentEngine.results.overallCapabilityIs', {
              band,
              score,
              scaleMax,
            })
          : t('assessmentEngine.results.yourCapabilityIs', {
              band,
              score,
              scaleMax,
            })}
      </p>

      {dimensionRows.length > 0 && (
        <ul className="m-0 grid list-none gap-1 p-0 sm:grid-cols-2">
          {dimensionRows.map((row) => (
            <li key={row.section_id} className="text-sm text-[var(--color-surface-text)]">
              {row.section_name} ({row.label})
            </li>
          ))}
        </ul>
      )}

      {(peer || maturity.model.label) && (
        <div className={`grid gap-2 ${peer ? 'sm:grid-cols-2' : ''} text-xs`}>
          {peer && (
            <p className="text-[var(--color-secondary-text)]">
              {t('assessmentEngine.results.peerCapabilityIs', {
                band: peer.overall.label?.trim() || '—',
                score: formatScore(peer.overall.mean_score ?? null),
                scaleMax,
              })}
            </p>
          )}
          <p className="text-[var(--color-secondary-text)]">
            {t('assessmentEngine.results.modelCapabilityIs', {
              band: maturity.model.label,
              min: modelMin,
              max: modelMax,
              scaleMax,
            })}
          </p>
        </div>
      )}
    </section>
  );
};
